import dayjs, { Dayjs } from 'dayjs'
import { match } from 'ts-pattern'
import { ChargeForecastPeriod, Plan } from '@/types'
import { RatePeriod } from '@/types/plugSessions'
import { RatePlanKind } from './ChargeForecastV2.types'

// Rates within 2% of each other are the same tier. Mirrors the backend's
// PlugSessionCostsService._classify_periods so an hour gets the same label
// here as on the plug session detail page.
const SAME_TIER_TOLERANCE = 0.02

// The backend's DEFAULT_REAL_TIME_COST_BOUNDARIES (ComEd's Save Money
// boundaries), used when a real-time plan has no cost_categories configured.
export const DEFAULT_REAL_TIME_COST_BOUNDARIES_CENTS: readonly number[] = [
  0, 8, 14,
]

const UPCOMING_RATES_HOURS = 24

export const RATE_PERIOD_LABELS: Record<RatePeriod, string> = {
  off_peak: 'Off-Peak',
  partial_peak: 'Partial-Peak',
  peak: 'Peak',
  flat: 'Flat rate',
  negative: 'Negative',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
}

const TOU_LEGEND_ORDER: RatePeriod[] = ['off_peak', 'partial_peak', 'peak']

export type RateBarSegment = {
  period: RatePeriod
  start: string
  end: string
  // Relative width of the segment: its duration in minutes
  weight: number
  // The tier's price, shown when the segment is tapped or hovered
  cents: number
}

export type RateLegendItem = {
  period: RatePeriod
  title: string
  detail: string
}

export type HourlyRateBar = {
  start: string
  cents: number
  period: RatePeriod
}

export type UpcomingRates = {
  variant: 'tou' | 'flat' | 'real_time'
  title: string
  // TOU and flat plans draw one continuous bar of rate segments
  segments: RateBarSegment[]
  // Real-time plans draw one bar per hour on a cents scale instead
  hourlyBars?: HourlyRateBar[]
  // Cents gridlines for the hourly chart: 0, the two thresholds, the ceiling
  yAxisTicksCents?: number[]
  axisLabels: string[]
  legend: RateLegendItem[]
}

export function getRatePlanKind(plan: Plan | null | undefined): RatePlanKind {
  if (!plan) {
    return 'none'
  }

  return match(plan)
    .with({ is_real_time: true }, () => 'real_time' as const)
    .with({ is_time_of_use: true }, () => 'tou' as const)
    .with({ is_tiered: true }, () => 'tiered' as const)
    .otherwise(() => 'flat' as const)
}

export function getPeriodCents(period: ChargeForecastPeriod) {
  return period.electricity_price_per_kwh * 100
}

/**
 * One decimal, the same as the House dashboard's rates (28.1¢), with a
 * trailing .0 dropped (39¢).
 */
export function formatCents(cents: number) {
  return `${roundCents(cents)}¢`
}

function roundCents(cents: number) {
  return Math.round(cents * 10) / 10
}

/** One price, or the cheapest to the priciest when a tier holds several. */
export function formatCentsRange(cents: number[]) {
  const low = Math.min(...cents)
  const high = Math.max(...cents)
  return roundCents(low) === roundCents(high)
    ? formatCents(low)
    : `${roundCents(low)} – ${formatCents(high)}`
}

/** Whether two prices would read as the same number on screen. */
export function isSameDisplayedPrice(a: number, b: number) {
  return roundCents(a) === roundCents(b)
}

/**
 * Buckets each rate into off_peak / partial_peak / peak.
 *
 * A plan with up to three prices ranks them: cheapest is off peak, priciest is
 * peak, anything between is partial peak. A forecast can hold more than three
 * prices, either because the plan has four or more rate levels or because the
 * window crosses a month or season rate change (September's 28.1 / 52.0 / 67.4
 * next to October's 27.4 / 46.6 / 49.3). Ranking those would call everything
 * but the two extremes partial peak, so they're grouped by value instead: the
 * sorted prices split at their two widest gaps into cheap, middle and
 * expensive. The label is only a colour; every hour keeps its own price.
 */
export function classifyTouRates(cents: number[]) {
  const knownRates: number[] = []
  const canonicalRates = cents.map((rate) => {
    const existing = knownRates.find(
      (known) =>
        known !== 0 &&
        Math.abs(rate - known) / Math.abs(known) <= SAME_TIER_TOLERANCE,
    )
    if (existing !== undefined) {
      return existing
    }
    knownRates.push(rate)
    return rate
  })

  const distinct = [...new Set(canonicalRates)].sort((a, b) => a - b)
  const [offPeakMax, partialPeakMax] = getTierCeilings(distinct)

  const periods = canonicalRates.map((rate) =>
    match(rate)
      .when(
        (r) => r <= offPeakMax,
        () => 'off_peak' as const,
      )
      .when(
        (r) => r <= partialPeakMax,
        () => 'partial_peak' as const,
      )
      .otherwise(() => 'peak' as const),
  )

  return { periods }
}

/**
 * The priciest rate still counted as off peak, and the priciest still counted
 * as partial peak, for a sorted list of distinct rates.
 */
function getTierCeilings(distinct: number[]): [number, number] {
  const cheapest = distinct[0]

  // One price is all off peak; two are off peak and peak
  if (distinct.length <= 2) {
    return [cheapest, cheapest]
  }
  if (distinct.length === 3) {
    return [cheapest, distinct[1]]
  }

  // Split at the two widest gaps between neighbouring prices
  const [firstSplit, secondSplit] = distinct
    .slice(1)
    .map((rate, index) => ({ index, gap: rate - distinct[index] }))
    .sort((a, b) => b.gap - a.gap || a.index - b.index)
    .slice(0, 2)
    .map(({ index }) => index)
    .sort((a, b) => a - b)

  return [distinct[firstSplit], distinct[secondSplit]]
}

/**
 * Classifies every forecast period's price at once, so the Upcoming Rates card
 * and the Charge Session Details rate tiers label the same hour the same way.
 */
export function classifyForecastRates(periods: ChargeForecastPeriod[]) {
  return classifyTouRates(periods.map(getPeriodCents))
}

/**
 * A real-time plan's Save Money cost-category boundaries in cents/kWh, or the
 * backend defaults when the plan has fewer than two.
 */
export function getRealTimeBoundariesCents(
  plan: Plan | null | undefined,
): readonly number[] {
  const categories = (plan?.cost_categories ?? []) as unknown[]
  const boundaries = categories
    .map((category) =>
      typeof category === 'number'
        ? category
        : (category as { max_cost_cents?: number } | null)?.max_cost_cents,
    )
    .filter((boundary): boundary is number => typeof boundary === 'number')

  return boundaries.length >= 2
    ? boundaries
    : DEFAULT_REAL_TIME_COST_BOUNDARIES_CENTS
}

/**
 * Mirrors the backend's PlugSessionCostsService._classify_real_time: below 0¢
 * is negative, then the two highest boundaries cap low and medium.
 */
/**
 * The two highest boundaries cap the low and medium real-time bands.
 */
export function getRealTimeThresholds(
  boundariesCents: readonly number[] = DEFAULT_REAL_TIME_COST_BOUNDARIES_CENTS,
) {
  const sorted = [...boundariesCents].sort((a, b) => a - b)
  return {
    lowMax: sorted[sorted.length - 2],
    mediumMax: sorted[sorted.length - 1],
  }
}

/**
 * The hourly chart's top gridline: the priciest hour rounded up to an even
 * cent, and always at least a little above the medium threshold.
 */
// A spike hour can be many times the normal price; scaling the axis to it
// would flatten every other hour, so the axis stops at twice the "high"
// boundary and taller bars are clipped and labelled instead
export const REAL_TIME_AXIS_CAP_MULTIPLIER = 2

export function getRealTimeCeilingCents(cents: number[], mediumMax: number) {
  const priciest = Math.max(...cents)
  const cap = mediumMax * REAL_TIME_AXIS_CAP_MULTIPLIER
  return Math.max(Math.min(Math.ceil(priciest / 2) * 2, cap), mediumMax + 2)
}

export function classifyRealTimeRate(
  cents: number,
  boundariesCents: readonly number[] = DEFAULT_REAL_TIME_COST_BOUNDARIES_CENTS,
): RatePeriod {
  const { lowMax, mediumMax } = getRealTimeThresholds(boundariesCents)

  return match(cents)
    .when(
      (c) => c < 0,
      () => 'negative' as const,
    )
    .when(
      (c) => c <= lowMax,
      () => 'low' as const,
    )
    .when(
      (c) => c <= mediumMax,
      () => 'medium' as const,
    )
    .otherwise(() => 'high' as const)
}

export function getUpcomingPeriods(
  periods: ChargeForecastPeriod[],
  now: Dayjs,
  hours = UPCOMING_RATES_HOURS,
) {
  const windowEnd = now.add(hours, 'hour')
  return periods.filter(
    (period) =>
      dayjs(period.end_datetime).isAfter(now) &&
      dayjs(period.start_datetime).isBefore(windowEnd),
  )
}

export function getAxisLabels(start: Dayjs, end: Dayjs) {
  const middle = start.add(end.diff(start) / 2, 'millisecond')
  return [start, middle, end].map((date) => date.format('ha'))
}

function toSegment(
  period: ChargeForecastPeriod,
  ratePeriod: RatePeriod,
): RateBarSegment {
  const cents = getPeriodCents(period)
  return {
    period: ratePeriod,
    start: dayjs(period.start_datetime).toISOString(),
    end: dayjs(period.end_datetime).toISOString(),
    cents,
    weight: Math.max(
      dayjs(period.end_datetime).diff(period.start_datetime, 'minute'),
      1,
    ),
  }
}

/**
 * Joins back-to-back periods that share a rate period and a price into one bar
 * segment, so a segment's price is always the real price of its hours.
 */
export function mergeRateSegments(segments: RateBarSegment[]) {
  return segments.reduce<RateBarSegment[]>((merged, segment) => {
    const previous = merged[merged.length - 1]
    if (
      previous &&
      previous.period === segment.period &&
      isSameDisplayedPrice(previous.cents, segment.cents)
    ) {
      return [
        ...merged.slice(0, -1),
        {
          ...previous,
          end: segment.end,
          weight: previous.weight + segment.weight,
        },
      ]
    }
    return [...merged, segment]
  }, [])
}

/**
 * Builds the "Upcoming Rates" card from the next 24 hours of the charge
 * forecast. Returns null when there's no rate plan or no upcoming prices.
 */
export function getUpcomingRates(args: {
  periods: ChargeForecastPeriod[]
  planKind: RatePlanKind
  now: Dayjs
  realTimeBoundariesCents?: readonly number[]
}): UpcomingRates | null {
  const { planKind, now, realTimeBoundariesCents } = args
  const allPeriods = args.periods ?? []
  const upcoming = getUpcomingPeriods(allPeriods, now)

  if (planKind === 'none' || !upcoming.length) {
    return null
  }

  const cents = upcoming.map(getPeriodCents)
  const axisLabels = getAxisLabels(
    dayjs(upcoming[0].start_datetime),
    dayjs(upcoming[upcoming.length - 1].end_datetime),
  )

  return match(planKind)
    .with('tou', (): UpcomingRates => {
      const { periods: ratePeriods } = classifyForecastRates(allPeriods)
      const upcomingRatePeriods = upcoming.map(
        (period) => ratePeriods[allPeriods.indexOf(period)],
      )
      // Every price the next 24 hours charge in each tier: one for a plain
      // three-rate plan, a range when a rate change falls inside the window
      const centsOf = (ratePeriod: RatePeriod) =>
        cents.filter((_, index) => upcomingRatePeriods[index] === ratePeriod)
      return {
        variant: 'tou',
        title: 'Upcoming Rates',
        segments: mergeRateSegments(
          upcoming.map((period, index) =>
            toSegment(period, upcomingRatePeriods[index]),
          ),
        ),
        axisLabels,
        legend: TOU_LEGEND_ORDER.filter(
          (period) => centsOf(period).length > 0,
        ).map((period) => ({
          period,
          title: RATE_PERIOD_LABELS[period],
          detail: `${formatCentsRange(centsOf(period))} per kWh`,
        })),
      }
    })
    .with('real_time', (): UpcomingRates => {
      const { lowMax, mediumMax } = getRealTimeThresholds(
        realTimeBoundariesCents,
      )
      return {
        variant: 'real_time',
        title: 'Upcoming Rates',
        segments: [],
        hourlyBars: upcoming.map((period, index) => ({
          start: dayjs(period.start_datetime).toISOString(),
          cents: cents[index],
          period: classifyRealTimeRate(cents[index], realTimeBoundariesCents),
        })),
        yAxisTicksCents: [
          0,
          lowMax,
          mediumMax,
          getRealTimeCeilingCents(cents, mediumMax),
        ],
        axisLabels,
        legend: [
          {
            period: 'low',
            title: `Under ${formatCents(lowMax)}`,
            detail: 'per kWh',
          },
          {
            period: 'medium',
            title: `${roundCents(lowMax)} – ${formatCents(mediumMax)}`,
            detail: 'per kWh',
          },
          {
            period: 'high',
            title: `Over ${formatCents(mediumMax)}`,
            detail: 'per kWh',
          },
        ],
      }
    })
    .with('flat', 'tiered', (): UpcomingRates => {
      const first = upcoming[0]
      const last = upcoming[upcoming.length - 1]
      return {
        variant: 'flat',
        title: 'Your Rate',
        segments: [
          {
            period: 'flat',
            start: dayjs(first.start_datetime).toISOString(),
            end: dayjs(last.end_datetime).toISOString(),
            weight: 1,
            cents: cents[0],
          },
        ],
        axisLabels,
        legend: [
          {
            period: 'flat',
            // A tiered plan is priced by usage, not by hour, so on this
            // card it reads the same as a flat one
            title: 'Flat rate',
            detail: `${formatCents(cents[0])} per kWh`,
          },
        ],
      }
    })
    .exhaustive()
}
